import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { DashboardProjectDto } from '@aiai/shared';
import { fetchMyProjects } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { workspacePath } from '../lib/workspacePath';
import { groupByWorkspace } from '../lib/groupByWorkspace';
import { useOrg } from '../org/useOrg';
import { useCapabilities } from '../hooks/useCapabilities';
import { PageHeader } from '../components/PageHeader';
import { IncomingTransfersBanner } from '../components/IncomingTransfersBanner';
import { NewProjectModal } from '../components/NewProjectModal';
import { WorkspaceGroupHeader } from '../components/WorkspaceGroupHeader';
import { Row, RowList } from '../components/Row';
import { ProjectStateBadge } from '../components/StatusBadge';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

function ProjectRow({ project }: { project: DashboardProjectDto }) {
  // One muted description line: repo · branch, then the open-issue count so the
  // backlog is scannable straight from the list.
  const { openIssueCount } = project;
  const openLabel = `${openIssueCount} open ${openIssueCount === 1 ? 'issue' : 'issues'}`;

  return (
    <Row
      to={workspacePath(project.workspace.slug, `/projects/${project.code ?? project.id}`)}
      badge={
        <ProjectStateBadge
          activeRuns={project.activeRunCount}
          hasGithubCredential={project.hasGithubCredential}
          size="sm"
        />
      }
      title={project.name}
      description={`${project.repoFullName} · ${project.defaultBranch} · ${openLabel}`}
    />
  );
}

export function ProjectsPage() {
  useDocumentTitle('Projects');
  const { orgs } = useOrg();
  const { workspacesWith } = useCapabilities();
  // Only workspaces the caller may manage projects in (owner)
  // AND whose plan includes repo projects are eligible create targets — a basic
  // (prototypes-only) workspace would be rejected server-side, so don't offer it.
  // (The server is still the authority; an admin override is resolved there.)
  const creatable = workspacesWith('project:manage').filter((o) => o.plan === 'pro');
  const canCreate = creatable.length > 0;
  const [modalOpen, setModalOpen] = useState(false);

  const projectsQuery = useQuery({
    queryKey: qk.myProjects,
    queryFn: fetchMyProjects,
  });

  const groups = useMemo(
    () => groupByWorkspace(projectsQuery.data ?? []),
    [projectsQuery.data],
  );

  // The create modal carries its own workspace selector, so opening it directly
  // is enough — it snaps the active workspace to an eligible one on open.
  const newButton = canCreate ? (
    <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
      <Plus className="h-4 w-4" aria-hidden="true" />
      New project
    </button>
  ) : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projects"
        subtitle="Your repos across every workspace, wired to agents."
        actions={newButton}
      />

      <IncomingTransfersBanner kind="repo" />

      {projectsQuery.isLoading ? (
        <LoadingState label="Loading projects…" />
      ) : projectsQuery.isError ? (
        <ErrorState
          message={errorMessage(projectsQuery.error, 'Failed to load projects')}
          onRetry={() => projectsQuery.refetch()}
        />
      ) : groups.length === 0 ? (
        <EmptyState
          title="No projects yet"
          description={
            canCreate
              ? 'Connect a GitHub repository to start handing off briefs to your agents.'
              : orgs.length === 0
                ? 'You are not a member of any workspace yet. Ask an admin to invite you.'
                : 'No projects here yet. A workspace owner can connect a repository.'
          }
          action={newButton}
        />
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.workspace.id}>
              <WorkspaceGroupHeader
                workspace={group.workspace}
                right={`${group.items.length} project${group.items.length === 1 ? '' : 's'}`}
              />
              <RowList>
                {group.items.map((p) => (
                  <ProjectRow key={p.id} project={p} />
                ))}
              </RowList>
            </div>
          ))}
        </div>
      )}

      {canCreate && (
        <NewProjectModal open={modalOpen} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}
